import React, { SetStateAction } from 'react';

import {
    Alert,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
} from '@mui/material';
import { Button } from '@apigrowfinance/pbolt-button';
import { PDialog, PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { AxiosError } from 'axios';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { DueDate } from '@clients/statementsClient';
import usePatchStatementDueDate from '@hooks/queries/usePutStatementDueDate';
import getEnumTranslation from '@utils/getEnumTranslation';

type ChangeStatementsDueDateModalProps = {
    accountId: string;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<SetStateAction<boolean>>;
    previousDay: number;
    availableDates: DueDate[];
};

export type ChangeDueDateInputProps = { id: number };

const ChangeStatementsDueDateModal = ({
    showEditModal,
    setShowEditModal,
    previousDay,
    accountId,
    availableDates,
}: ChangeStatementsDueDateModalProps) => {
    const methods = useForm({
        shouldUnregister: false,
        mode: 'onChange',
        defaultValues: {
            id: previousDay,
        },
    });
    const {
        control,
        handleSubmit,
        register,
        formState: { isDirty },
    } = methods;
    const { mutate, error, isError } = usePatchStatementDueDate(
        parseFloat(accountId),
        () => {
            setShowEditModal(false);
        },
    );

    const onSubmit = (data: ChangeDueDateInputProps) => {
        mutate(data);
    };

    return (
        <FormProvider {...methods}>
            <PDialog
                open={showEditModal}
                dialogTitle={
                    <FormattedMessage
                        id="ChangeStatementsDueDateModal.title"
                        defaultMessage="Edit statement due date"
                    />
                }
                dialogSubtitle={
                    <FormattedMessage
                        id="ChangeStatementsDueDateModal.subtitle"
                        defaultMessage="You can only edit the statement due date once every statement period."
                    />
                }
                onClose={() => setShowEditModal(false)}>
                <form
                    data-testid="statement-due-date-edit"
                    onSubmit={handleSubmit(onSubmit)}>
                    <PDialogContent>
                        <Controller
                            name="id"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <FormControl
                                    variant="outlined"
                                    sx={{ width: '100%' }}>
                                    <InputLabel id="due-date-select-label">
                                        <FormattedMessage
                                            id="ChangeStatementsDueDateModal.statementDueDate"
                                            defaultMessage="Statement due date"
                                        />
                                    </InputLabel>
                                    <Select
                                        {...register('id')}
                                        labelId="due-date-select-label"
                                        data-testid="due-date-dropdown"
                                        fullWidth
                                        defaultValue={previousDay}
                                        value={value}
                                        label={
                                            <FormattedMessage
                                                id="ChangeStatementsDueDateModal.statementDueDate"
                                                defaultMessage="Statement due date"
                                            />
                                        }
                                        onChange={onChange}
                                        input={
                                            <OutlinedInput
                                                label={
                                                    <FormattedMessage
                                                        id="ChangeStatementsDueDateModal.statementDueDate"
                                                        defaultMessage="Statement due date"
                                                    />
                                                }
                                            />
                                        }>
                                        {availableDates.map((date) => (
                                            <MenuItem
                                                key={date?.program_due_date_id}
                                                value={
                                                    date?.program_due_date_id
                                                }>
                                                <ListItemText
                                                    primary={date?.day}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {isError && (
                            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                                {(error as AxiosError)?.response?.data?.message}
                            </Alert>
                        )}
                        {isDirty && !isError && (
                            <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                                <FormattedMessage
                                    id="ChangeStatementsDueDateModal.change"
                                    defaultMessage="This change will effect the next statement."
                                />
                            </Alert>
                        )}
                    </PDialogContent>
                    <PDialogAction>
                        <Button
                            onClick={() => {
                                setShowEditModal(false);
                            }}>
                            {getEnumTranslation('cancel')}
                        </Button>
                        <Button
                            disabled={!isDirty}
                            type="submit"
                            variant="contained">
                            {getEnumTranslation('save')}
                        </Button>
                    </PDialogAction>
                </form>
            </PDialog>
        </FormProvider>
    );
};

export default ChangeStatementsDueDateModal;
